import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
interface NewsItem {
  title: string;
  description: string;
  link: string;
  pubDate: Date;
  source: string;
}
const FEEDS = [{
  name: 'CoinDesk',
  url: 'https://www.coindesk.com/arc/outboundfeeds/rss/?outputType=xml'
}, {
  name: 'CoinTelegraph',
  url: 'https://cointelegraph.com/rss'
}, {
  name: 'CryptoSlate',
  url: 'https://cryptoslate.com/feed/'
}, {
  name: 'CryptoNews',
  url: 'https://cryptonews.com/news/feed/'
}, {
  name: 'CryptoPotato',
  url: 'https://cryptopotato.com/feed/'
}];
const RSS2JSON_BASE = 'https://api.rss2json.com/v1/api.json?rss_url=';
const MAX_ITEMS = 10;
const SLIDE_INTERVAL = 8000;
function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
export function CryptoNewsBar() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    loadNews();
  }, []);
  useEffect(() => {
    if (newsItems.length > 0) {
      resetTimer();
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [newsItems, currentIndex]);
  async function fetchFeed(feed: (typeof FEEDS)[0]): Promise<NewsItem[]> {
    const url = RSS2JSON_BASE + encodeURIComponent(feed.url);
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch ' + feed.name);
    const data = await res.json();
    if (data.status !== 'ok') throw new Error('Bad status for ' + feed.name);
    return data.items.map((item: any) => ({
      title: item.title,
      description: stripHtml(item.description || item.content || ''),
      link: item.link,
      pubDate: new Date(item.pubDate || item.pubdate || item.date || Date.now()),
      source: feed.name
    }));
  }
  async function loadNews() {
    try {
      const results = await Promise.allSettled(FEEDS.map(fetchFeed));
      let allItems: NewsItem[] = [];
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          allItems = allItems.concat(result.value);
        }
      });
      allItems.sort((a, b) => b.pubDate.getTime() - a.pubDate.getTime());
      allItems = allItems.slice(0, MAX_ITEMS);
      if (allItems.length > 0) {
        setNewsItems(allItems);
        setError(false);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }
  function resetTimer() {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % newsItems.length);
    }, SLIDE_INTERVAL);
  }
  function goToSlide(index: number) {
    setCurrentIndex(index);
    resetTimer();
  }
  function nextSlide() {
    setCurrentIndex(prev => (prev + 1) % newsItems.length);
    resetTimer();
  }
  function prevSlide() {
    setCurrentIndex(prev => (prev - 1 + newsItems.length) % newsItems.length);
    resetTimer();
  }
  if (loading) {
    return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-900 to-slate-900 text-white py-4 px-6 shadow-lg z-50 border-t border-blue-800" data-id="element-45">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3" data-id="element-46">
          <div className="flex gap-1" data-id="element-47">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
            animationDelay: '0ms'
          }} data-id="element-48"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
            animationDelay: '150ms'
          }} data-id="element-49"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
            animationDelay: '300ms'
          }} data-id="element-50"></div>
          </div>
          <span className="text-sm text-blue-200 animate-pulse" data-id="element-51">
            Getting most up-to-date crypto opportunities...
          </span>
        </div>
      </div>;
  }
  if (error || newsItems.length === 0) {
    return null;
  }
  const currentItem = newsItems[currentIndex];
  return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white shadow-2xl z-50 border-t border-blue-800/50" data-id="element-52">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-53">
        <div className="flex items-center gap-4" data-id="element-54">
          {/* Navigation Buttons */}
          <button onClick={prevSlide} className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Previous news" data-id="element-55">
            <ChevronLeftIcon size={18} data-id="element-56" />
          </button>

          {/* News Content */}
          <div className="flex-1 min-w-0" data-id="element-57">
            <div className="flex items-start gap-3" data-id="element-58">
              <div className="flex-shrink-0" data-id="element-59">
                <div className="bg-blue-500/20 text-blue-300 text-xs font-semibold px-2 py-1 rounded" data-id="element-60">
                  CRYPTO NEWS
                </div>
              </div>

              <div className="flex-1 min-w-0" data-id="element-61">
                <h3 className="font-semibold text-sm mb-1 line-clamp-1" data-id="element-62">
                  {currentItem.title}
                </h3>
                <div className="flex items-center gap-2 text-xs text-slate-300 mb-2" data-id="element-63">
                  <span className="font-medium text-blue-300" data-id="element-64">
                    {currentItem.source}
                  </span>
                  <span data-id="element-65">•</span>
                  <span data-id="element-66">{currentItem.pubDate.toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 mb-2" data-id="element-67">
                  {currentItem.description.slice(0, 200)}
                  {currentItem.description.length > 200 ? '...' : ''}
                </p>
                <a href={currentItem.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors" data-id="element-68">
                  <span data-id="element-69">Read full article</span>
                  <ExternalLinkIcon size={12} data-id="element-70" />
                </a>
              </div>
            </div>
          </div>

          <button onClick={nextSlide} className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Next news" data-id="element-71">
            <ChevronRightIcon size={18} data-id="element-72" />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex items-center justify-center gap-1.5 mt-3" data-id="element-73">
          {newsItems.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-1.5 rounded-full transition-all ${index === currentIndex ? 'w-6 bg-blue-400' : 'w-1.5 bg-slate-600 hover:bg-slate-500'}`} aria-label={`Go to news ${index + 1}`} data-id="element-74" />)}
        </div>
      </div>
    </div>;
}